/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1669593600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Mon Nov 28 2022 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "amobee",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Getting started",
          "depth": 0,
          "id": "getting-started"
        }, {
          "value": "Setup on Amobee",
          "depth": 1,
          "id": "setup-on-amobee"
        }, {
          "value": "Setup on Hightouch",
          "depth": 0,
          "id": "setup-on-hightouch"
        }, {
          "value": "Syncing data",
          "depth": 0,
          "id": "syncing-data"
        }, {
          "value": "Configuration file",
          "depth": 1,
          "id": "configuration-file"
        }, {
          "value": "Taxonomy file",
          "depth": 1,
          "id": "taxonomy-file"
        }, {
          "value": "Data file format and encoding",
          "depth": 1,
          "id": "data-file-format-and-encoding"
        }, {
          "value": "Columns to sync",
          "depth": 1,
          "id": "columns-to-sync"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      p: "p",
      h3: "h3",
      code: "code",
      ul: "ul",
      li: "li",
      a: "a",
      img: "img"
    }, _provideComponents(), props.components), {NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Any data set"
            }), _jsx(_components.td, {
              children: "Sync data from a source to a tab-delimited Amobee file"
            }), _jsx(_components.td, {
              children: "Insert only"
            })]
          })
        })]
      }), "\n", _jsx(_components.p, {
        children: "This destination ignores the concept of additions, changes, removals. Syncs always send the full results of your model. For example, if your model has 1,000 records, your Amobee data file will be 1,000 records."
      }), "\n", _jsx(_components.h2, {
        id: "getting-started",
        children: "Getting started"
      }), "\n", _jsx(_components.h3, {
        id: "setup-on-amobee",
        children: "Setup on Amobee"
      }), "\n", _jsxs(_components.p, {
        children: ["The initial setup process involves getting assigned a ", _jsx(_components.code, {
          children: "market ID"
        }), " from Amobee. If you don't know this ID, reach out to your Amobee representative."]
      }), "\n", _jsx(_components.h2, {
        id: "setup-on-hightouch",
        children: "Setup on Hightouch"
      }), "\n", _jsx(_components.p, {
        children: "To get started with the Amobee destination on Hightouch, you must provide the market ID."
      }), "\n", _jsx(_components.h2, {
        id: "syncing-data",
        children: "Syncing data"
      }), "\n", _jsx(_components.p, {
        children: "In addition to the data file, Hightouch sends configuration and taxonomy files during the sync. Amobee's system picks up the files hourly.\nAmobee also emails a success or failure notification to the address you provide in the sync configuration."
      }), "\n", _jsx(_components.h3, {
        id: "configuration-file",
        children: "Configuration file"
      }), "\n", _jsx(_components.p, {
        children: "To create the configuration file, enter the following information into the Hightouch during sync setup:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Advertiser ID"
        }), "\n", _jsx(_components.li, {
          children: "Email address to receive success notifications"
        }), "\n", _jsx(_components.li, {
          children: "Name of the data file you want to send to Amobee"
        }), "\n", _jsx(_components.li, {
          children: "Classification symbol"
        }), "\n", _jsx(_components.li, {
          children: "Data file unique identifier ID type"
        }), "\n", _jsx(_components.li, {
          children: "Data file start date"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Hightouch uses this information to construct the configuration file included in the sync."
      }), "\n", _jsx(_components.h3, {
        id: "taxonomy-file",
        children: "Taxonomy file"
      }), "\n", _jsxs(_components.p, {
        children: ["You also need to upload a taxonomy file as part of the sync configuration form. An Amobee taxonomy file is an Excel document that helps categorize data into a meaningful format.\nThe accepted file types include: ", _jsx(_components.code, {
          children: ".xls"
        }), " and ", _jsx(_components.code, {
          children: ".xlsx"
        }), ". For reference, you can check out this ", _jsx(_components.a, {
          href: "https://bit.ly/3UzkGP5",
          children: "taxonomy file example"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-amobee-taxonomy-file.png",
          alt: "Taxonomy File input in Hightouch"
        })
      }), "\n", _jsx(_components.h3, {
        id: "data-file-format-and-encoding",
        children: "Data file format and encoding"
      }), "\n", _jsxs(_components.p, {
        children: ["The data file must be delimited, with fields and categories separated. Categories need to be\nseparated using a comma (", _jsx(_components.code, {
          children: ","
        }), ")."]
      }), "\n", _jsx(_components.h3, {
        id: "columns-to-sync",
        children: "Columns to sync"
      }), "\n", _jsx(_components.p, {
        children: "For this destination, you can export all columns represented in your model."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-amobee-sync-columns.png",
          alt: "Selecting \"Sync all columns\" in Hightouch exports all columns in your model"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["If you need to remap the fields you're exporting, you can do so manually.\nIn that case, the sync only exports fields you've mapped. This example exports ", _jsx(_components.code, {
          children: "ID"
        }), " and ", _jsx(_components.code, {
          children: "CategoryID"
        }), " and ignores all other fields in the model."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-amobee-add-mappings.png",
          alt: "Mapping ID and Category ID fields"
        })
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
